import { type PicoXConfiguration } from '../models/configuration';
import { type StoredEvent } from '../models/event';

// Current version of the database.
const DB_VERSION = 1;
// The name of the object store holding the events.
const EVENT_STORE_NAME = 'events';
// The path used to reference the timestamp when querying events ordered by timestamp.
const TIMESTAMP_EVENT_PATH = 'event_timestamp';

/**
 * Persistence implements methods to store and retrieve events from the database.
 *
 * It exports a method 'storeEvent' which stores an event in the database.
 * It exports a method 'retrieveEvents' which retrieves all stored events from the database.
 * It exports a method 'removeEvents' which removes a list of events from the database.
 *
 * This logic has been taken from `@bendingspoons/pico-web`, which currently uses IndexedDB.
 */
export type Persistence = {
  /** Stores an event */
  storeEvent: (event: StoredEvent) => Promise<void>;

  /** Retrieves all stored events */
  retrieveEvents: (batchSize: number) => Promise<StoredEvent[]>;

  /** Removes events */
  removeEvents: (events: StoredEvent[]) => Promise<void>;
};

/**
 * Creates and returns a Persistence implementation using IndexedDB.
 *
 * This implementation handles:
 * - Storing events in IndexedDB with automatic cleanup when storage limit is reached
 * - Retrieving events in batches ordered by timestamp
 * - Removing specific events by ID
 *
 * The database is initialized with a single object store 'events' that has an index on the
 * event_timestamp field for efficient timestamp-based queries.
 *
 * When storing events, if the total number of stored events exceeds maxEventsStored from the config,
 * the oldest events are automatically deleted to maintain targetEventsNumberAfterClearingSpace.
 *
 * @param configuration The PicoX configuration containing:
 *   - dbName: Name of the IndexedDB database to use
 *   - maxEventsStored: Maximum number of events to store before cleanup
 *   - targetEventsNumberAfterClearingSpace: Target number of events after cleanup
 * @returns A Promise that resolves to a Persistence implementation with storeEvent, retrieveEvents and removeEvents methods
 */
export const createPersistence = async (
  configuration: PicoXConfiguration,
): Promise<Persistence> => {
  const cachedDB: IDBDatabase = await openDatabase(configuration.dbName);

  const storeEvent = async (event: StoredEvent) => {
    const db = await ensureConnection(configuration.dbName, cachedDB);
    const totalEvents = await countValues(db, EVENT_STORE_NAME);
    if (totalEvents >= configuration.maxEventsStored) {
      const eventsToDelete = totalEvents - configuration.targetEventsNumberAfterClearingSpace;
      const events = await getAllFromIndex<StoredEvent>(
        db,
        EVENT_STORE_NAME,
        TIMESTAMP_EVENT_PATH,
        eventsToDelete,
        'next',
      );
      await deleteEvents(events);
    }
    await storeValue<StoredEvent>(db, EVENT_STORE_NAME, event.event_id, event);
  };

  const retrieveEvents = async (batchSize: number) => {
    const db = await ensureConnection(configuration.dbName, cachedDB);
    const events = await getAllFromIndex<StoredEvent>(
      db,
      EVENT_STORE_NAME,
      TIMESTAMP_EVENT_PATH,
      batchSize,
      'next',
    );
    return events;
  };

  /**
   * Deletes a list of events from the event store.
   * @param events The array of StoredEvent objects to delete.
   * @returns A Promise that resolves when the events are deleted.
   */
  const deleteEvents = async (events: StoredEvent[]) => {
    const keys = events.map((event) => event.event_id);
    const db = await ensureConnection(configuration.dbName, cachedDB);
    await deleteValues(db, EVENT_STORE_NAME, keys);
  };

  return {
    storeEvent,
    retrieveEvents,
    removeEvents: deleteEvents,
  };
};

/**
 * Opens an IndexedDB database with the specified configuration.
 *
 * This is a Promise wrapper around the IndexedDB open request API. It handles:
 * - Opening a connection to the database
 * - Creating/upgrading the database schema via onupgradeneeded
 * - Error handling during open
 *
 * @param databaseName The name of the database to open
 * @param version The schema version number
 * @param onUpgradeCallBack Callback function to handle database upgrades, called during onupgradeneeded
 * @returns Promise resolving to the opened IDBDatabase instance
 */
const openDb = async (
  databaseName: string,
  version: number,
  onUpgradeCallBack: (db: IDBDatabase) => void,
) => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, version);
    request.onerror = () => {
      reject(request.error);
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onupgradeneeded = () => {
      const db = request.result;
      onUpgradeCallBack(db);
    };
  });
};

/**
 * Stores or updates a value in an IndexedDB object store.
 *
 * @param db The IndexedDB database instance
 * @param objectStore Name of the object store to write to
 * @param key The unique key to store the value under
 * @param value The value to store
 * @returns Promise that resolves when the write completes
 */
const storeValue = async <T>(
  db: IDBDatabase,
  objectStore: string,
  key: string,
  value: T,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(objectStore, 'readwrite');
    const store = tx.objectStore(objectStore);
    const request = store.put(value, key);

    request.onerror = () => {
      reject(request.error);
    };
    request.onsuccess = () => {
      resolve();
    };
  });
};

/**
 * Retrieves values from an IndexedDB store using an index.
 *
 * @param db The IndexedDB database instance
 * @param objectStore Name of the object store to query
 * @param index Name of the index to query on
 * @param limit Maximum number of results to return
 * @param resultOrdering Direction to traverse the index ('next' or 'prev')
 * @returns Promise resolving to array of retrieved values
 */
const getAllFromIndex = async <T>(
  db: IDBDatabase,
  objectStore: string,
  index: string,
  limit: number,
  resultOrdering: IDBCursorDirection,
): Promise<T[]> => {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(objectStore, 'readonly');
    const store = tx.objectStore(objectStore);
    const request = store.index(index).openCursor(null, resultOrdering);
    const result: T[] = [];

    request.onerror = () => {
      reject(request.error);
    };
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor && result.length < limit) {
        result.push(cursor.value);
        cursor.continue();
      } else {
        resolve(result);
      }
    };
  });
};

/**
 * Counts the total number of records in an IndexedDB object store.
 *
 * @param db The IndexedDB database instance
 * @param objectStore Name of the object store to count
 * @returns Promise resolving to the count of records
 */
const countValues = async (db: IDBDatabase, objectStore: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(objectStore, 'readonly');
    const store = tx.objectStore(objectStore);
    const request = store.count();

    request.onerror = () => {
      reject(request.error);
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
  });
};

/**
 * Deletes multiple values from an IndexedDB object store by their keys.
 *
 * @param db The IndexedDB database instance
 * @param objectStore Name of the object store to delete from
 * @param keys Array of keys to delete
 * @returns Promise that resolves when all deletions complete
 */
const deleteValues = async (
  db: IDBDatabase,
  objectStore: string,
  keys: string[],
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(objectStore, 'readwrite');
    const store = tx.objectStore(objectStore);
    const promises: Promise<void>[] = [];

    keys.forEach((key) => {
      const request = store.delete(key);
      promises.push(
        new Promise((resolve, reject) => {
          request.onerror = () => {
            reject(request.error);
          };
          request.onsuccess = () => {
            resolve();
          };
        }),
      );
    });

    Promise.all(promises)
      .then(() => resolve())
      .catch(reject);
  });
};

/**
 * Checks if an IndexedDB database connection is still valid and open.
 *
 * Tests the connection by attempting to start a transaction.
 * An InvalidStateError indicates the connection is closed.
 *
 * @param db The IndexedDB database instance to check
 * @returns true if connection is valid, false otherwise
 */
const isOpen = (db: IDBDatabase): boolean => {
  try {
    db.transaction([EVENT_STORE_NAME], 'readwrite');
    return true;
  } catch (err) {
    const error = err as Error;
    return error.name !== 'InvalidStateError';
  }
};

/**
 * Opens and initializes an IndexedDB database with the required schema.
 *
 *
 * @param dbName Name of the database to open
 * @returns Promise resolving to the opened database
 */
const openDatabase = async (dbName: string): Promise<IDBDatabase> => {
  return openDb(dbName, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(EVENT_STORE_NAME)) {
      const store = db.createObjectStore(EVENT_STORE_NAME);
      store.createIndex(TIMESTAMP_EVENT_PATH, 'event_timestamp');
    }
  });
};

/**
 * Ensures a valid database connection exists, creating a new one if needed.
 *
 *
 * @param dbName Name of the database to connect to
 * @param cachedDB Current cached database connection
 * @returns Promise resolving to a valid database connection
 */
const ensureConnection = async (dbName: string, cachedDB: IDBDatabase): Promise<IDBDatabase> => {
  if (cachedDB && isOpen(cachedDB)) return cachedDB;
  cachedDB = await openDatabase(dbName);
  return cachedDB;
};
